import { useEffect } from "react";
import { Navbar } from "@/components/sps/Navbar";
import { Footer } from "@/components/sps/Footer";

const IMG = {
  historia:
    "https://customer-assets-39nsmqrw.emergentagent.net/job_spirits-production/artifacts/1ghwazp7_FONDO_HISTORIA.jpeg",
  vision:
    "https://customer-assets.emergentagent.com/job_spirits-production/artifacts/tb3yrbm1_FONDO%20VISI%C3%93N.webp",
  mision:
    "https://customer-assets-39nsmqrw.emergentagent.net/job_spirits-production/artifacts/4qopp33i_Mision-Nueva.webp",
  valores:
    "https://customer-assets.emergentagent.com/job_spirits-production/artifacts/zfowpwtc_FOTO%20VALORES.webp",
};

const VALORES = [
  {
    title: "Pasión por el Tequila:",
    desc: "Vivimos y honramos la tradición tequilera mexicana en cada proyecto.",
  },
  {
    title: "Autenticidad:",
    desc: "Respetamos el origen del agave y creamos marcas con alma.",
  },
  {
    title: "Innovación con Propósito:",
    desc: "Buscamos nuevas formas de transformar ideas en experiencias únicas.",
  },
  {
    title: "Respeto por la Tierra:",
    desc: "Valoramos y protegemos el entorno que nos da vida.",
  },
  {
    title: "Comunidad:",
    desc: "Creemos en el poder de generar desarrollo local y oportunidades reales para nuestra gente.",
  },
];

export default function NosotrosPage() {
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  return (
    <div className="App min-h-screen bg-white text-[#002438] relative">
      <Navbar />
      <main className="relative">
        {/* HISTORIA — background image with dark tint + text overlay */}
        <section
          id="historia"
          data-testid="historia-section"
          className="relative min-h-screen flex items-center overflow-hidden"
        >
          <div className="absolute inset-0 z-0">
            <img
              src={IMG.historia}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-32 md:py-40 w-full">
            <div className="max-w-2xl">
              <h1
                data-testid="historia-title"
                className="font-sans-modern font-medium text-white text-4xl md:text-5xl lg:text-6xl leading-[1.1] tracking-tight mb-4"
              >
                Historia SPS
              </h1>
              <div className="h-[3px] w-16 bg-white/70 mb-10" />
              <div className="space-y-6 text-[#e5e7eb] text-base md:text-lg font-light leading-relaxed font-sans-modern">
                <p>
                  SPS nace en 2022 al ver una oportunidad que iba más allá del
                  negocio: resolver una necesidad real de la industria del
                  tequila. El envasado, convertido en uno de los principales
                  retos para marcas y productores, fue el punto de partida de
                  una visión compartida.
                </p>
                <p>
                  Ese mismo año, cinco socios unieron experiencia, talento y
                  compromiso para construir una empresa diferente. Mientras la
                  industria suele iniciar por la producción, SPS decidió
                  comenzar por donde más se necesitaba: una planta de envasado
                  en Milpillas, Jalisco. Esta decisión respondió a una lectura
                  clara del mercado y a la necesidad de ofrecer capacidad,
                  eficiencia y servicio en una etapa crítica para muchas
                  marcas y productores. Después llegó la destilería en Unión
                  de San Antonio.
                </p>
                <p>
                  Hoy, con una tercera planta en desarrollo en Arandas, SPS
                  sigue creciendo con más de 370 colaboradores, tres plantas
                  y una oficina corporativa. Una historia construida con
                  visión, trabajo y propósito.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* MISIÓN — split: left text on white / right harvester photo */}
        <section
          id="mision"
          data-testid="mision-section"
          className="relative bg-white"
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[420px] lg:min-h-[600px]">
            <div
              className="lg:col-span-6 flex items-center px-6 md:px-12 lg:pr-12 py-10 md:py-12 lg:py-12"
              style={{ paddingLeft: "max(1.5rem, calc((100vw - 80rem) / 2 + 6rem))" }}
            >
              <div className="max-w-2xl">
                <h2
                  data-testid="mision-title"
                  className="font-sans-modern font-medium text-[#002438] leading-[1.1] tracking-tight uppercase mb-2"
                  style={{ fontSize: "3rem" }}
                >
                  Misión
                </h2>
                <div className="h-[3px] w-12 bg-[#002438]/70 mb-6" />
                <div
                  className="space-y-4 text-[#3a4651] font-light font-sans-modern"
                  style={{ fontSize: "1.125rem", lineHeight: "1.75rem" }}
                >
                  <p>
                    Acompañamos a visionarios y emprendedores para convertir
                    <br />
                    sus ideas en marcas de tequila y derivados de agave,
                    cuidando cada detalle: desde la tierra donde nace el agave
                    hasta la botella que llega al mundo.
                  </p>
                  <p>
                    Nuestra misión es innovar con propósito, honrar la
                    tradición tequilera y crear impacto positivo en las
                    comunidades que nos rodean.
                  </p>
                </div>
              </div>
            </div>
            <div className="lg:col-span-6 relative min-h-[280px] lg:min-h-0 pl-4 pt-4 pb-4 pr-0 lg:pl-6 lg:pt-6 lg:pb-6 lg:pr-0">
              <img
                src={IMG.mision}
                alt="Trabajador agavero recolectando plantas de agave"
                className="w-full h-full object-cover rounded-l-2xl"
              />
            </div>
          </div>
        </section>

        {/* VISIÓN — full-bleed piñas image with dark tint and text overlay */}
        <section
          id="vision"
          data-testid="vision-section"
          className="relative min-h-screen flex items-center overflow-hidden"
        >
          <div className="absolute inset-0 z-0">
            <img
              src={IMG.vision}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-24 md:py-32 w-full">
            <div className="max-w-2xl">
              <h2
                data-testid="vision-title"
                className="font-sans-modern font-medium text-white leading-[1.1] tracking-tight uppercase mb-3"
                style={{ fontSize: "3rem" }}
              >
                Visión
              </h2>
              <div className="h-[3px] w-14 bg-white/70 mb-8" />
              <div
                className="space-y-6 text-[#e5e7eb] font-light font-sans-modern"
                style={{ fontSize: "1.125rem", lineHeight: "1.75rem" }}
              >
                <p>
                  Ser el corazón que impulsa la creación de marcas de
                  <br />
                  tequila y derivados de agave que conquisten el mundo.
                </p>
                <p>
                  Queremos liderar un movimiento que une innovación,
                  <br />
                  tradición y sostenibilidad, cerrando el ciclo del agave
                  <br />
                  para dejar una huella duradera en la industria, la tierra
                  <br />
                  y las personas.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* VALORES — split: left text on white / right bottling photo */}
        <section
          id="valores"
          data-testid="valores-section"
          className="relative bg-white"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[700px]">
            <div
              className="flex items-center px-6 md:px-12 lg:pr-12 py-20 md:py-28 lg:py-32"
              style={{ paddingLeft: "max(1.5rem, calc((100vw - 80rem) / 2 + 6rem))" }}
            >
              <div className="max-w-2xl">
                <h2
                  data-testid="valores-title"
                  className="font-sans-modern font-medium text-[#002438] leading-[1.1] tracking-tight uppercase mb-3"
                  style={{ fontSize: "3rem" }}
                >
                  Valores
                </h2>
                <div className="h-[3px] w-14 bg-[#002438]/70 mb-10" />
                <ul className="space-y-6">
                  {VALORES.map((v, i) => (
                    <li
                      key={v.title}
                      data-testid={`valor-item-${i}`}
                      className="pl-4 border-l-[3px] border-[#002438]"
                    >
                      <div>
                        <span
                          className="text-[#002438] font-sans-modern"
                          style={{ fontSize: "1.125rem", lineHeight: "1.75rem", fontWeight: 500 }}
                        >
                          {v.title}
                        </span>
                      </div>
                      <p
                        className="mt-1 text-[#002438] font-light font-sans-modern"
                        style={{ fontSize: "1.125rem", lineHeight: "1.75rem" }}
                      >
                        {v.desc}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="relative min-h-[420px] lg:min-h-0 pl-5 pt-5 pb-5 pr-0">
              <img
                src={IMG.valores}
                alt="Colaboradora de SPS inspeccionando botellas de vidrio"
                className="w-full h-full object-cover rounded-l-2xl"
              />
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
